import React, { useState } from 'react'

import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, Divider, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { brainstormText } from 'content/brainstormText'
import isEqual from 'lodash/isEqual'
import { parseOverviewChallengeContent } from 'utils/parseOverviewChallengeContent'
import { validatePersonaInput } from 'utils/validatePersonaField'

import {
	SimonSaysPersonaExampleIdea,
	SimonSaysPersonaResponse,
	SimonSaysPromptResponse,
} from 'models/ideaModels'

import { useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import PromptCarouselComponent from 'pages/RoundRobin/components/Carousel'

import FacilitationHeader from 'components/FacilitationHeader'
import GeneratedExampleDropdown from 'components/GeneratedExampleDropdown'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import { styleProps, styles } from './Section2.styles'

export const TEST_ID = 'simon-says-section2'

export type Props = {
	createPrompt: () => void
	currentResponses: string[]
	customer: string
	generateAiImage: () => Promise<void>
	generateImageDisabled: () => boolean
	imageAvailable: boolean
	imageData: string | null
	isAiImageLoading: boolean
	isCurrentResponseLoading: boolean
	loading: boolean
	manualChangeCustomerPrompts: (newCustomer: string) => void
	onRegenerateExampleIdeas: (
		personas: SimonSaysPersonaResponse[],
		personaIndex: number
	) => Promise<boolean>
	randomElement: string
	resetImage: () => void
	simonSaysPrompt?: SimonSaysPromptResponse
}

const Section2 = ({ simonSaysPrompt, ...props }: Props) => {
	const { showAlert } = useToastContext()
	const { showModal } = useModalContext()
	const [businessComparisonIndex, setBusinessComparisonIndex] = useState(0)
	const [showAIexampleIdea, setShowAIexampleIdea] = useState(false)
	const [temporalPromptsEditMode, setTemporalPrompts] = useState<string[]>([])

	// Edit state management for persona editing
	const [isEditMode, setIsEditMode] = useState(false)
	const [isRegeneratingExamples, setIsRegeneratingExamples] = useState(false)
	const [originalPersonas, setOriginalPersonas] = useState<
		SimonSaysPersonaResponse[]
	>([])
	const [editedPersonas, setEditedPersonas] = useState<
		SimonSaysPersonaResponse[]
	>([])

	const onClickRefresh = () => {
		props.createPrompt()
	}

	// Helper functions for state management operations
	const initializeEditState = (personas: SimonSaysPersonaResponse[]) => {
		setOriginalPersonas([...personas])
		setEditedPersonas([...personas])
	}

	const resetEditState = () => {
		setOriginalPersonas([])
		setEditedPersonas([])
	}

	const hasUnsavedChanges = () => {
		return (
			JSON.stringify(originalPersonas) !== JSON.stringify(editedPersonas)
		)
	}

	const restoreOriginalPersonas = () => {
		setEditedPersonas([...originalPersonas])
	}

	const cleanChallenges = (challenges: string[]): string[] =>
		challenges
			.map((challenge) => challenge.trim())
			.filter((challenge) => challenge.length > 0)

	const updatePersonaField = (
		personaIndex: number,
		overview: string,
		challenges: string[]
	): void => {
		setEditedPersonas((prevState) => {
			const cleanedChallenges = cleanChallenges(challenges)

			return prevState.map((persona, index) =>
				index === personaIndex
					? {
							...persona,
							overview: isEqual(persona.overview, overview)
								? persona.overview
								: overview,
							challenges: isEqual(
								persona.challenges,
								cleanedChallenges
							)
								? persona.challenges
								: cleanedChallenges,
						}
					: persona
			)
		})
	}

	// Ensure proper data mapping between display and edit formats
	const getDisplayPersonaData = () => {
		return isEditMode
			? editedPersonas
			: simonSaysPrompt?.data?.personas || []
	}

	// Legacy support for existing carousel integration
	const setPromptValue = (value: string) => {
		const newTemporalPrompts = [...temporalPromptsEditMode]
		newTemporalPrompts[businessComparisonIndex] = value
		setTemporalPrompts(newTemporalPrompts)

		const { overview, challenges } = parseOverviewChallengeContent(value)
		updatePersonaField(businessComparisonIndex, overview, challenges)
	}

	const currentPrompt = simonSaysPrompt?.data?.personas
		? simonSaysPrompt.data.personas[businessComparisonIndex]
		: undefined

	const buildResponseItem = (): React.ReactElement | undefined => {
		if (!currentPrompt) {
			return undefined
		}

		return (
			<Box sx={styles.aiResponseContainer}>
				<Typography component="span" sx={styles.aiResponseRole}>
					{`${currentPrompt.role} - ${currentPrompt.name}`}
				</Typography>

				<ul data-testid="gen-ai-response-list">
					{currentPrompt.exampleIdeas?.map(
						(el: SimonSaysPersonaExampleIdea) => (
							<Box key={`ai-solution-group-${el.topic}`}>
								<Typography
									component="li"
									sx={styles.aiResponseTopic}
									{...styleProps.responseTopic}
								>
									{el.topic}
								</Typography>
								<ul>
									{el.subpoints.map((subpoint: string) => (
										<Typography
											component="li"
											key={`ai-solution-${el.topic}-${subpoint.slice(0, 20)}`}
											sx={styles.aiResponseSubpoint}
										>
											{subpoint}
										</Typography>
									))}
								</ul>
							</Box>
						)
					)}
				</ul>
			</Box>
		)
	}

	// Get the appropriate persona data based on edit mode
	const displayPersonas = getDisplayPersonaData()

	// Format persona content for display and editing with proper structure
	const currentPrompts = displayPersonas.map(
		(persona) =>
			persona.overview +
			'\n\n' +
			persona.challenges
				.map((challenge, i) => `${i + 1}. ${challenge}`)
				.join('\n')
	)

	// Create names array using persona role and name information
	const personaNames = displayPersonas.map(
		(persona) => `${persona.role} - ${persona.name}`
	)

	const onClickEdit = () => {
		if (simonSaysPrompt?.data?.personas) {
			initializeEditState(simonSaysPrompt.data.personas)
			setIsEditMode(true)
			// Initialize temporal prompts with current persona data for editing
			const editPrompts = simonSaysPrompt.data.personas.map(
				(persona) =>
					persona.overview +
					'\n\n' +
					persona.challenges
						.map((challenge, i) => `${i + 1}. ${challenge}`)
						.join('\n')
			)
			setTemporalPrompts(editPrompts)
		}
	}

	const onClickCancelEdit = () => {
		if (hasUnsavedChanges()) {
			showModal('WARNING_MODAL', {
				onProceed: () => {
					restoreOriginalPersonas()
					setIsEditMode(false)
					resetEditState()
				},
				onCancel: () => {
					// User chose to keep editing, do nothing
				},
			})
		} else {
			setIsEditMode(false)
			resetEditState()
		}
	}

	const onClickSaveEdit = async () => {
		if (editedPersonas.length > 0) {
			const persona = editedPersonas[businessComparisonIndex]
			const { overview, challenges } = persona

			const validation = validatePersonaInput(overview, challenges)
			if (!validation.isValid && validation.error) {
				if (showAlert) {
					showAlert({
						objectType: 'defaultError',
						severity: ToastSeverity.ERROR,
						bodyText: validation.error,
					})
				}

				return
			}

			setIsRegeneratingExamples(true)
			const proceeded = await props.onRegenerateExampleIdeas(
				editedPersonas,
				businessComparisonIndex
			)

			if (!proceeded) {
				setIsRegeneratingExamples(false)
				return
			}

			setIsRegeneratingExamples(false)
		}
		setShowAIexampleIdea(false)
		setIsEditMode(false)
		resetEditState()
		if (showAlert) {
			showAlert({
				objectType: 'editPrompt',
				severity: ToastSeverity.SUCCESS,
			})
		}
	}

	return (
		<>
			<Section2Title gameType="simonSays" />
			<UserEducationModal />
			<Box sx={styles.root}>
				<Box sx={styles.sideBar} />
				<Box sx={styles.content}>
					<Box sx={styles.headerSection}>
						<Box sx={styles.introRow}>
							<Typography
								data-testid={`${TEST_ID}__intro-text`}
								{...styleProps.introText}
								sx={styles.introText}
							>
								To inform your ideation, consider the following
								perspectives different stakeholder personas
								might have as it relates to your innovation
								topic.
							</Typography>
						</Box>
						<FacilitationHeader
							onRefresh={onClickRefresh}
							onEdit={isEditMode ? undefined : onClickEdit}
							isLoading={props.loading}
							refreshTestId={`${TEST_ID}__refresh-button`}
							editTestId={`${TEST_ID}__edit-button`}
							refreshIconElement={
								<RefreshIcon {...styleProps.refreshIcon} />
							}
						/>
					</Box>
					<Box sx={styles.mainContent}>
						{props.loading ? (
							<LoadingSpinner />
						) : (
							<>
								<Box sx={styles.carouselWrapper}>
									<Box
										sx={styles.buttonWrapper}
										className="button-wrapper"
									>
										{simonSaysPrompt?.data?.personas &&
											simonSaysPrompt.data.personas.map(
												(persona, index) => (
													<React.Fragment
														key={`persona-${persona.name}`}
													>
														<Button
															data-testid={`${TEST_ID}__business-comparisons-${persona.name}`}
															className={
																index ===
																businessComparisonIndex
																	? 'selected'
																	: ''
															}
															onClick={() =>
																setBusinessComparisonIndex(
																	index
																)
															}
															disabled={
																isEditMode
															}
														>
															PERSONA {index + 1}
														</Button>
														{index <
															simonSaysPrompt.data
																.personas
																.length -
																1 && (
															<Divider
																{...styleProps.personaDivider}
															/>
														)}
													</React.Fragment>
												)
											)}
									</Box>
								</Box>
								<Box
									data-testid={`${TEST_ID}__business-comparison-traits`}
								>
									{simonSaysPrompt && (
										<PromptCarouselComponent
											disabledArrows={isEditMode}
											setCurrentIndex={
												setBusinessComparisonIndex
											}
											currentIndex={
												businessComparisonIndex
											}
											currentPrompts={currentPrompts}
											promptsEditMode={
												temporalPromptsEditMode
											}
											editable={isEditMode}
											setPromptValue={setPromptValue}
											names={personaNames}
											sxCardOverrides={
												styles.promptCardOverrides
											}
										/>
									)}
								</Box>
							</>
						)}
					</Box>
					{isEditMode ? (
						<Box sx={styles.editActions}>
							<Button
								{...styleProps.saveButton}
								data-testid={`${TEST_ID}__edit-save-button`}
								onClick={onClickSaveEdit}
								disabled={isRegeneratingExamples}
							>
								<Typography>SAVE CHANGES</Typography>
							</Button>
							<Button
								{...styleProps.cancelButton}
								data-testid={`${TEST_ID}__edit-cancel-button`}
								onClick={onClickCancelEdit}
							>
								<Typography>CANCEL</Typography>
							</Button>
						</Box>
					) : null}
					{!!props.currentResponses && (
						<>
							<Typography
								{...styleProps.ideasDropdownText}
								sx={styles.ideasDropdownText}
							>
								{brainstormText.global.ideasDropdown}
							</Typography>
							<GeneratedExampleDropdown
								gameTypeEnum={GAME_TYPE.SIMON_SAYS}
								headerText={''}
								isLoading={
									props.loading ||
									props.isCurrentResponseLoading
								}
								customResponse={buildResponseItem()}
								showAIExampleIdea={showAIexampleIdea}
								setShowAIExampleIdea={setShowAIexampleIdea}
							/>
						</>
					)}
				</Box>
			</Box>
		</>
	)
}
export default Section2
